import { AxiosInstance } from 'axios';
import type { TimesheetDashboardRow } from '../../types';

export function createTimesheetApi(client: AxiosInstance) {
  return {
    async createTimesheet(week_start_date: string, rows: any[], supervisor_id?: number, project_id?: number | null, template_id?: number) {
      const response = await client.post('/timesheets', { week_start_date, rows, supervisor_id, project_id, template_id });
      return response.data;
    },
    async getTimesheets(params?: { week_start?: string; week_end?: string }) {
      const response = await client.get('/timesheets', { params });
      return response.data;
    },
    async getTimesheetsPaginated(params: { limit: number; before_date?: string; before_id?: number; supervisor_id?: number; project_id?: number }) {
      const q: Record<string, string> = { limit: String(params.limit) };
      if (params.before_date !== undefined) q.before_date = params.before_date;
      if (params.before_id   !== undefined) q.before_id   = String(params.before_id);
      if (params.supervisor_id !== undefined) q.supervisor_id = String(params.supervisor_id);
      if (params.project_id !== undefined) q.project_id = String(params.project_id);
      const qs = new URLSearchParams(q).toString();
      const response = await client.get(`/timesheets?${qs}`);
      return response.data as { timesheets: any[]; next_cursor: { date: string; id: number } | null };
    },
    async getTimesheetDetail(id: number) {
      const response = await client.get(`/timesheets/${id}`);
      return response.data;
    },
    async exportTimesheet(id: number, language = 'en') {
      const response = await client.get(`/timesheets/${id}/export`, { responseType: 'blob', params: { format: 'pdf', language } });
      return response.data as Blob;
    },
    async updateTimesheetRows(id: number, rows: any[], expectedUpdatedAt?: string) {
      const response = await client.patch(`/timesheets/${id}`, {
        rows,
        expected_updated_at: expectedUpdatedAt,
      });
      return response.data;
    },
    async updateTimesheetWeek(id: number, week_start_date: string, expectedUpdatedAt?: string) {
      const response = await client.patch(`/timesheets/${id}/week`, {
        week_start_date,
        expected_updated_at: expectedUpdatedAt,
      });
      return response.data;
    },
    async submitTimesheet(id: number) {
      const response = await client.post(`/timesheets/${id}/submit`);
      return response.data;
    },
    async approveTimesheet(id: number) {
      const response = await client.post(`/timesheets/${id}/approve`);
      return response.data;
    },
    async rejectTimesheet(id: number, rejection_reason?: string) {
      const response = await client.post(`/timesheets/${id}/reject`, { rejection_reason });
      return response.data;
    },
    async reopenTimesheet(id: number, reopen_reason: string) {
      const response = await client.post(`/timesheets/${id}/reopen`, { reopen_reason });
      return response.data;
    },
    async deleteTimesheet(id: number) {
      const response = await client.delete(`/timesheets/${id}`);
      return response.data;
    },
    async getTimesheetHistory(id: number) {
      const response = await client.get(`/timesheets/${id}/history`);
      return response.data;
    },
    async deleteTimesheetRow(rowId: number) {
      const response = await client.delete(`/timesheets/rows/${rowId}`);
      return response.data;
    },
    async getTimesheetDashboardSummary(options?: { suppressAuthRedirect?: boolean }) {
      const response = await client.get('/timesheets/dashboard-summary', {
        suppressAuthRedirect: Boolean(options?.suppressAuthRedirect),
      } as any);
      return response.data as { timesheets: TimesheetDashboardRow[]; status_counts: Record<string, number> };
    },
  };
}

export type TimesheetApi = ReturnType<typeof createTimesheetApi>;
